import { LookupTool } from "commonAssets/components/Forms/LookupTool";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { outputDiv, i18n, loading } from "commonAssets/js/constants";

/**
 * Extended LookupTool with custom deadline support.
 * 
 * If a custom deadline is provided, it performs a client-side date check
 * and skips the API-based deadline check (whether the custom deadline has passed or not).
 * If no deadline is provided, it falls back to the API-based claim deadline check.
 */
export class CustomLookupTool extends LookupTool {
    /**
     * @param {Object} options
     * @param {string} options.configPath
     * @param {string} options.userLanguage
     * @param {import("commonAssets/utils/ErrorHandler").ErrorHandler} options.errorHandler
     * @param {Date|string|null} [options.deadline] - Optional custom deadline date
     */
    constructor({ configPath, userLanguage, errorHandler, deadline = null }) {
        super({ configPath, userLanguage, errorHandler });
        this.deadline = deadline ? new Date(deadline) : null;
    }

    /**
     * Check if the custom deadline has passed
     * @returns {{ passed: boolean, error?: string }}
     */
    isDeadlinePassed() {
        if (!this.deadline) {
            return { passed: false };
        }
        
        const now = new Date();
        if (now > this.deadline) {
            return { 
                passed: true, 
                error: "The deadline to submit a Claim has passed." 
            };
        }
        return { passed: false };
    }

    /**
     * Override renderPage to support custom deadline.
     * If a custom deadline is provided and has passed, shows an error message.
     * Otherwise, calls parent's renderPage, skipping the API deadline check
     * only when a custom deadline is set.
     * 
     * @param {Object} params
     * @param {Function} params.onLookupSuccess
     * @param {Function} params.onLookupError
     * @returns {Promise<void>}
     */
    async renderPage({ onLookupSuccess, onLookupError }) {
        try {
            // Check custom deadline first (client-side)
            const customDeadlineCheck = this.isDeadlinePassed();
            if (customDeadlineCheck.passed) {
                outputDiv.appendChild(
                    createElement(
                        "div",
                        {
                            class: "text-center text-danger fw-bold fs-4 py-4",
                            "aria-label": customDeadlineCheck.error,
                        },
                        [],
                        `${customDeadlineCheck.error} <br> ${i18n.getTranslation("return_to_home")}`
                    )
                );
                loadingGif(loading, "hide", 250);
                return;
            }

            // Call parent's renderPage, skipping the API deadline check only if a custom deadline is set
            await super.renderPage({ 
                onLookupSuccess, 
                onLookupError, 
                skipDeadlineCheck: this.deadline !== null 
            });
        } catch (error) {
            this.errorHandler.log_exception(
                new Error("Could not render lookup page", { cause: error })
            );
            this.errorHandler.display(error);
            loadingGif(loading, "hide", 250);
        }
    }
}
